import { StatusData } from "@fairfleet/geotab";
import { QueryDiagnosticDeviceData } from "@/models/QueryDiagnosticDeviceData";

/**
 * Builds the routing key for one (diagnostic, device) series.
 *
 * Encoded as JSON rather than joined with a separator, because Geotab ids are opaque and may
 * contain any character a separator could use.
 */
export function historicalStatusDataKey(diagnosticId: string, deviceId: string): string {
  return JSON.stringify([diagnosticId, String(deviceId)]);
}

export function parseHistoricalStatusDataKey(key: string): {
  diagnosticId: string;
  deviceId: string;
} {
  const [diagnosticId, deviceId] = JSON.parse(key) as [string, string];
  return { diagnosticId, deviceId };
}

export interface HistoricalStatusDataCollector {
  /** The nested result, filled in as pages arrive. */
  readonly result: QueryDiagnosticDeviceData;
  /** Files one page of records under the series its key identifies. */
  addPage(key: string, page: StatusData[]): void;
}

/**
 * Collects `StatusData` pages into the nested `{diagnostic: {device: records}}` shape.
 *
 * Every requested combination gets an entry up front, so consumers can index into the result
 * without checking whether a device reported anything at all.
 *
 * @param diagnosticIds - The requested diagnostics.
 * @param deviceIds - The requested devices.
 */
export function collectHistoricalStatusData(
  diagnosticIds: string[],
  deviceIds: string[],
): HistoricalStatusDataCollector {
  const result: QueryDiagnosticDeviceData = {};

  for (const diagnosticId of diagnosticIds) {
    result[diagnosticId] = {};
    for (const deviceId of deviceIds) {
      // Coerced the same way a property access would coerce it, so a caller passing a missing
      // device id still reads back the bucket it was filed under.
      result[diagnosticId][String(deviceId)] = [];
    }
  }

  return {
    result,

    addPage(key, page) {
      const { diagnosticId, deviceId } = parseHistoricalStatusDataKey(key);
      const series = result[diagnosticId]?.[deviceId];
      if (series === undefined) {
        throw new Error(
          `Received a StatusData page for an unrequested series: diagnostic ${diagnosticId} / device ${deviceId}`,
        );
      }
      for (const record of page) {
        series.push({ data: record.data, dateTime: record.dateTime });
      }
    },
  };
}
